import {
  type CreateSalesOrderDTO,
  Currency,
  isFieldErrorResponse,
  PricingTypeEnum,
  useApiAccountsV1UserRetrieve,
  useApiOrdersV1SalesOrdersCreate,
  WhoFixesDateEnum,
} from 'api';
import { dayjs } from 'date';
import { type AnyObject, type Flags, Form, type InferType, type ObjectSchema, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { useRef } from 'react';
import { Alert, Box, Button, Page, Section } from 'ui';

import type { UploadDocumentProps } from '../../../document';
import { SalesOrderFormSections } from '../../components';
import { SalesOrderFormContextProvider } from '../../contexts/SalesOrderFormContext';
import { useUploadAndCreateSalesOrderDocument } from '../../hooks';
import { createSalesOrderSchema } from '../../schemas/createSalesOrderSchema';
import { convertToOrderInputMaterialComponents } from '../../utils/materialComponentValue';
import { redirectToSalesOrderConfirmationPage } from '../../utils/redirectToSalesOrderConfirmationPage';
import { scrollToFirstError } from '../../utils/scrollToFirstError';

export type SalesOrderCreateFormValues = InferType<typeof createSalesOrderSchema>;
export type SalesOrderCreateFormInputValues =
  typeof createSalesOrderSchema extends ObjectSchema<infer TInput, AnyObject, unknown, Flags> ? TInput : never;

const DEFAULT_VALUES: Partial<SalesOrderCreateFormValues> = {
  buyer_currency: Currency.USD,
  deposit_percentage: '0',
  // @ts-expect-error This errors because the type does not recognize 'n/a' as a valid value as the BE expects undefined for `n/a`, but in our selects we cannot use undefined
  dirt_level: 'n/a',
  due_date_direction: 1,
  due_date_offset: 0,
  free_time_required: 0,
  is_food_grade: false,
  is_trial_order: false,
  is_variable_pricing: false,
  price_per_tonne: Currency.USD,
  seller_material_component_values: [],
  // We can't set the value from e2e tests so we set it to a default value
  target_delivery_date: import.meta.env.VITE_CI ? dayjs().toISOString() : undefined,
  third_party_commission_currency: Currency.USD,
  third_party_commission_per_tonne: '0',
  tonnage_variance: '10',
};

export const SalesOrderCreate = () => {
  const { t } = useTranslation('orders');
  const form = useForm<SalesOrderCreateFormInputValues, unknown, SalesOrderCreateFormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(createSalesOrderSchema),
  });
  const { setError, getValues, formState } = form;
  const { errors: formErrors } = formState;
  const { data: user } = useApiAccountsV1UserRetrieve();
  const uploadAndCreateDocument = useUploadAndCreateSalesOrderDocument();
  const { mutate: createOrder, isPending } = useApiOrdersV1SalesOrdersCreate({
    mutation: {
      onError(error) {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            setError(field as keyof SalesOrderCreateFormValues, { message: error.response.data[field] });
          });

          requestAnimationFrame(() => {
            scrollToFirstError();
          });
        }
        if (error.response?.data.non_field_errors) {
          setError('root', { message: error.response.data.non_field_errors.toString() });
          requestAnimationFrame(() => {
            window.scrollTo(0, 0);
          });
        }
      },
      async onSuccess(data) {
        const documents = getValues().documents;
        if (documents) {
          await Promise.all(
            documents?.map((document: UploadDocumentProps) => uploadAndCreateDocument(document, { slug: data.slug }))
          );
        }
        redirectToSalesOrderConfirmationPage(data.slug);
      },
    },
  });

  const alertRef = useRef<HTMLDivElement>(null);

  const onSubmit = async (values: SalesOrderCreateFormValues) => {
    if (!user) {
      throw new Error('User not found');
    }
    const dueDateOffset = (values.due_date_offset || 0) * values.due_date_direction;
    const targetDeliveryDate = dayjs(values.target_delivery_date).format('YYYY-MM-DD');
    const fixingDate =
      values.who_fixes_date && values.fixing_date ? dayjs(values.fixing_date).format('YYYY-MM-DD') : undefined;
    const pricingType = values.is_variable_pricing ? PricingTypeEnum.INDEX : PricingTypeEnum.FIXED;

    const materialComponentValues = convertToOrderInputMaterialComponents(values.buyer_material_component_values || []);

    const orderData: CreateSalesOrderDTO = {
      consignee_id: values.end_processor_id,
      created_by_id: user.id,
      ...values,
      due_date_offset: dueDateOffset,
      fixing_date: fixingDate,
      material_component_values: materialComponentValues,
      pricing_type: pricingType,
      target_delivery_date: targetDeliveryDate,
    };

    if (pricingType === PricingTypeEnum.INDEX) {
      orderData.preliminary_price = values.price_per_tonne;
    }

    if (!values.is_variable_pricing) {
      (
        [
          'index',
          'price_as_percentage_of_index',
          'cash_or_three_month',
          'who_fixes_date',
          'fixing_date',
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
          'price_point',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    if (values.who_fixes_date === WhoFixesDateEnum.CUSTOMER || values.who_fixes_date === WhoFixesDateEnum.SAFI) {
      orderData.fixing_date = undefined;
    } else {
      (
        [
          'fixing_date_start_event',
          'fixing_date_start_offset',
          'fixing_date_end_event',
          'fixing_date_end_offset',
        ] as const
      ).forEach((field) => {
        orderData[field] = undefined;
      });
    }

    createOrder({
      data: orderData,
    });
  };

  const onError = () => {
    requestAnimationFrame(() => {
      scrollToFirstError();
    });
  };

  return (
    <Page>
      <Page.Title title={t('salesOrderCreatePage.title')} subtitle={t('salesOrderCreatePage.subtitle')} />
      <Page.Divider />
      <Section>
        <SalesOrderFormContextProvider variant="create" documents={[]} defaultValues={undefined}>
          <Form<SalesOrderCreateFormInputValues, unknown, SalesOrderCreateFormValues>
            {...form}
            onSubmit={onSubmit}
            onError={onError}
            devtool={false}
          >
            {formErrors.root ? (
              <Alert ref={alertRef} severity="error" sx={{ scrollPadding: '160px' }}>
                {formErrors.root.message}
              </Alert>
            ) : null}
            <SalesOrderFormSections />
            <Box
              sx={{
                alignItems: 'center',
                justifyContent: 'end',
                display: 'flex',
                paddingTop: 4,
              }}
            >
              <Button type="submit" size="large" loading={isPending}>
                {t('salesOrderCreatePage.submit')}
              </Button>
            </Box>
          </Form>
        </SalesOrderFormContextProvider>
      </Section>
    </Page>
  );
};
